import React, { useMemo, useState } from "react";
import InputField from "../../components/Input/Inputfeild";
import "./UserSelects.css";

/**
 * Who a WhatsApp or SMS schedule goes to: a name and a number, added one at a time.
 *
 * ── WHY THIS IS NOT A MODE ON RecipientSelect ───────────────────────────────
 * They look alike — a field, then removable chips — but they are different
 * interactions, not two skins of one:
 *
 *   RecipientSelect   one field, free text, commits on Enter / comma / semicolon /
 *                     blur, splits a pasted list into several, and optionally searches
 *                     a `users` list.
 *   this              two fields and an explicit Add, because a name and a number
 *                     cannot be told apart from one string, and a pasted list of pairs
 *                     has no sensible separator.
 *
 * Folding both into one component would mean a second field and a second commit path
 * appearing conditionally inside something whose whole shape is "type and it becomes a
 * chip" — and it would put the email flow at risk, which ShareReportModal also uses.
 *
 * The chip, avatar and field styling is shared through UserSelects.css, so the two read
 * as the same control.
 *
 * ── THE SHAPE IT PRODUCES ───────────────────────────────────────────────────
 * `{ id, name, phone, external }` — the same shape RecipientSelect emits, minus the
 * email. buildSchedulePayload turns it into the scheduler's
 * `{ Name, PhoneNumber }` objects.
 */

/* Loose on purpose, like RecipientSelect's EMAIL_RE: this is a "did you mean to type a
   number" check, not a claim that the line exists or that the country code is right. */
const PHONE_RE = /^\+?\d{6,15}$/;

/* Stored without the punctuation people type, so "+94 77 123 4567", "+94-77-123-4567"
   and "+94771234567" are one number rather than three. */
const normalise = (raw) => String(raw || "").replace(/[\s()-]/g, "");

/* Digits make poor initials, so a phone chip gets the name's initials where there is a
   name and a neutral glyph where there is not. */
const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const SWATCHES = ["#7c3aed", "#059669", "#2563eb", "#d97706", "#db2777", "#0891b2", "#dc2626", "#16a34a"];
const colorFor = (seed) => {
  let hash = 0;
  for (let i = 0; i < String(seed).length; i += 1) hash = (hash * 31 + String(seed).charCodeAt(i)) % 9973;
  return SWATCHES[hash % SWATCHES.length];
};

export default function PhoneRecipientSelect({ value = [], onChange }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");

  /* Keyed on the number, not on the name — two people can share a name, and the number
     is what the message is actually sent to. */
  const chosen = useMemo(() => new Set(value.map((r) => normalise(r.phone))), [value]);

  const add = () => {
    const cleaned = normalise(phone);

    if (!cleaned) {
      setError("Enter a phone number.");
      return;
    }
    if (!PHONE_RE.test(cleaned)) {
      setError("Enter a valid phone number, e.g. +94771234567");
      return;
    }
    if (chosen.has(cleaned)) {
      // Already there - clearing the fields is the whole response; saying so would nag.
      setName("");
      setPhone("");
      setError("");
      return;
    }

    onChange([...value, { id: `phone-${cleaned}`, name: name.trim(), phone: cleaned, external: true }]);
    setName("");
    setPhone("");
    setError("");
  };

  const remove = (target) => {
    onChange(value.filter((r) => normalise(r.phone) !== target));
  };

  /* Enter adds from either field, so the whole thing is usable without reaching for the
     mouse. Not a form submit - this sits inside the Create Schedule page and submitting
     would publish the schedule. */
  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      add();
    }
  };

  return (
    <div className="csh-recipients">
      <div className="csh-phone-fields">
        <InputField
          type="text"
          placeholder="Name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <InputField
          type="tel"
          placeholder="Phone number..."
          className={error ? "input-error" : ""}
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            if (error) setError("");
          }}
          onKeyDown={onKeyDown}
        />
        <button type="button" className="csh-phone-add" onClick={add} disabled={!phone.trim()}>
          Add
        </button>
      </div>

      {error ? <div className="csh-recipient-error">{error}</div> : null}

      {/* The same list, chip and remove classes RecipientSelect uses, so the two controls
          are indistinguishable once something has been added. is-external because every
          phone entry is typed in rather than picked from a directory. */}
      {value.length > 0 ? (
        <ul className="csh-recipient-list">
          {value.map((r) => {
            const key = normalise(r.phone);
            return (
              <li className="csh-recipient-chip is-external" key={key}>
                <span className="csh-avatar" style={{ background: colorFor(key) }}>
                  {initialsOf(r.name) || "#"}
                </span>
                {/* The number always shows: it is what the message goes to, and two
                    people with one name would otherwise be indistinguishable. */}
                <span className="csh-recipient-chip-name">
                  {r.name ? `${r.name} (${r.phone})` : r.phone}
                </span>
                <button
                  type="button"
                  className="csh-recipient-remove"
                  aria-label={`Remove ${r.name || r.phone}`}
                  onClick={() => remove(key)}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
